import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import Peer from 'simple-peer';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { FaThumbsUp, FaUserFriends, FaComment, FaArrowLeft, FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaRecordVinyl, FaStop, FaTimes, FaDesktop, FaClosedCaptioning } from 'react-icons/fa';
import { SERVER_URL, WS_URL } from '../config';
import VideoGrid from './VideoGrid';

// Styled components
const StreamContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--gradient-bg);
`;

const StreamHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-sm);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary);
  font-weight: 500;
  transition: all var(--transition-fast);
  
  &:hover {
    transform: translateX(-3px);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const MeetingInfoPrefix = styled.span`
  font-weight: 700;
  color: var(--primary-color);
  margin-right: 8px;
  font-size: 0.9em;
`;

const StreamTitle = styled.h1`
  margin: 0 auto;
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50%;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    max-width: 60%;
  }
`;

const StreamHost = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const MainContent = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
`;

const VideoSection = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  position: relative;
`;

const VideoContainer = styled.div`
  flex-grow: 1;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background-color: #000;
  box-shadow: var(--shadow-lg);
  position: relative;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StreamControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  margin-top: 1rem;
`;

const LikeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: ${props => props.liked ? 'rgba(250, 77, 86, 0.1)' : 'rgba(15, 98, 254, 0.1)'};
  color: ${props => props.liked ? '#ff4d4f' : 'var(--primary)'};
  border-radius: var(--radius-md);
  font-weight: 500;
  transition: all var(--transition-fast);
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    transform: scale(1.05);
    background: ${props => props.liked ? 'rgba(250, 77, 86, 0.2)' : 'rgba(15, 98, 254, 0.2)'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CommentButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: rgba(15, 98, 254, 0.1);
  color: var(--primary);
  border-radius: var(--radius-md);
  font-weight: 500;
  transition: all var(--transition-fast);
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    transform: scale(1.05);
    background: rgba(15, 98, 254, 0.2);
  }
`;

const SidePanel = styled.div`
  width: 320px;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-sm);
  
  @media (max-width: 768px) {
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 100;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform var(--transition-normal);
  }
`;

const ViewersList = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    color: var(--text-primary);
    
    svg {
      color: var(--primary);
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 200px;
    overflow-y: auto;
  }
`;

const ViewerItem = styled.li`
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--success);
    margin-right: 0.75rem;
  }
  
  &.new-viewer {
    animation: highlight 2s ease-out;
  }
  
  @keyframes highlight {
    0% { background-color: rgba(15, 98, 254, 0.1); }
    100% { background-color: transparent; }
  }
`;

const CommentsSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  
  h3 {
    margin: 1.5rem 1.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    color: var(--text-primary);
    
    svg {
      color: var(--primary);
    }
  }
`;

const CommentsList = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0 1.5rem;
`;

const CommentItem = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
`;

const CommentUser = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--primary);
`;

const CommentText = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
`;

const CommentTime = styled.div`
  font-size: 0.8rem;
  color: var(--text-tertiary);
  margin-top: 0.25rem;
  text-align: right;
`;

const CommentForm = styled.form`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem 1.5rem;
`;

const CommentInput = styled.div`
  background-color: #f0f2f5;
  padding: 0.5rem;
  border-top: 1px solid #ddd;
  
  input {
    flex-grow: 1;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 20px;
    margin-right: 0.5rem;
  }
  
  button {
    padding: 0.5rem 1rem;
    background-color: #4267B2;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  transition: all var(--transition-fast);
  
  &:hover {
    background: var(--primary-dark);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const MobileSidePanelToggle = styled.button`
  display: none;
  position: absolute;
  right: 1.5rem;
  bottom: 1.5rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  font-size: 1.2rem;
  box-shadow: var(--shadow-md);
  z-index: 10;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  border-radius: var(--radius-md);
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: var(--shadow-md);
  z-index: 5;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  
  svg {
    color: var(--primary);
  }
  
  &.likes {
    svg {
      color: #ff4d4f;
    }
  }
`;

const LikeAnimation = styled.div`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2rem;
  color: #ff4d4f;
  pointer-events: none;
  animation: float-up 1.5s forwards;
  opacity: 0;
  
  @keyframes float-up {
    0% { opacity: 0; transform: translate(-50%, 0); }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { opacity: 0; transform: translate(-50%, -100px); }
  }
`;

const ControlItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1.5rem;
  color: #606770;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const MediaControlsContainer = styled.div`
  display: flex;
  margin-left: auto;
`;

const MediaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f0f2f5;
  border: 1px solid #ddd;
  margin-left: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #e4e6eb;
  }
  
  svg {
    font-size: 1.2rem;
    color: #606770;
  }
`;

const RecordingControls = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: 1rem;
`;

const RecordingIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #ff0000;
  margin-right: 8px;
  animation: pulse 1.5s infinite;
  
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const RecordingTime = styled.span`
  font-family: monospace;
  margin-right: 8px;
`;

const CommentPopup = styled.div`
  position: absolute;
  bottom: 70px;
  right: 20px;
  width: 300px;
  height: 400px; /* Fixed height instead of max-height */
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  z-index: 100;
  overflow: hidden;
`;

const CommentPopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  border-bottom: 1px solid #ddd;
`;

const CommentPopupTitle = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Modal animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInNotification = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

// Add global style for notifications
const GlobalStyle = createGlobalStyle`
  .screen-sharing-notification {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 10px 20px;
    border-radius: 4px;
    z-index: 1000;
    animation: ${fadeInNotification} 0.3s ease-out forwards, ${fadeOut} 0.3s ease-in forwards 2.7s;
  }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// Modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const ConfirmationModal = styled.div`
  width: 100%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: ${scaleIn} 0.3s ease;
`;

const ModalContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  font-family: 'Inter', sans-serif;
`;

const ModalSubtext = styled.p`
  margin: 0 0 2rem 0;
  color: var(--text-secondary);
  text-align: center;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  justify-content: center;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-normal);
  font-family: 'Inter', sans-serif;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const CancelButton = styled(Button)`
  background: var(--neutral-200);
  color: var(--text-secondary);
  border: none;
  
  &:hover {
    background: var(--neutral-300);
  }
`;

const LeaveButton = styled(Button)`
  background: var(--error);
  color: white;
  border: none;
  box-shadow: var(--shadow-sm);
  
  &:hover {
    background: #e03e48;
    box-shadow: 0 0 15px rgba(250, 77, 86, 0.5);
  }
`;
const ViewStream = ({ username: propsUsername }) => {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get username from props or location state
  const username = propsUsername || location.state?.username;
  
  const [socket, setSocket] = useState(null);
  const [stream, setStream] = useState(null);
  const [viewers, setViewers] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [likes, setLikes] = useState([]);
  const [likeCount, setLikeCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [hasLiked, setHasLiked] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenStreamRef = useRef(null);
  
  const videoRef = useRef();
  const peerRef = useRef();
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  
  // Add state for transcripts
  const [showTranscripts, setShowTranscripts] = useState(false);
  const [transcripts, setTranscripts] = useState([]);
  
  // Add state for remote participants
  const [participants, setParticipants] = useState([]);
  const [hostStream, setHostStream] = useState(null);
  
  // Add state for permissions
  const [permissions, setPermissions] = useState({
    allowViewerAudio: true,
    allowViewerVideo: true,
    allowViewerChat: true,
    allowViewerScreenShare: true
  });
  
  // Add state for permission errors
  const [permissionError, setPermissionError] = useState(null);
  
  useEffect(() => {
    if (!username) {
      navigate('/');
      return;
    }
    
    // Get user media with improved error handling
    // Around line 390 after getting the local stream
    const getUserMedia = async () => {
      try {
        // Check if media devices are available
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Media devices not supported in this browser');
        }

        // Request permission with clear constraints
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            channelCount: 2,
            // Add advanced noise suppression options
            googEchoCancellation: true,
            googAutoGainControl: true,
            googNoiseSuppression: true,
            googHighpassFilter: true,
            googTypingNoiseDetection: true,
            googNoiseReduction: true
          }
        });
        
        if (!stream) {
          throw new Error('Failed to get media stream');
        }

        console.log("Local stream obtained successfully:", stream);
        
        // Ensure video track is enabled
        if (stream.getVideoTracks().length > 0) {
          stream.getVideoTracks()[0].enabled = true;
        }
        
        // Set local stream
        setLocalStream(stream);
        
        // Update state to reflect enabled tracks
        setIsAudioEnabled(stream.getAudioTracks()[0]?.enabled || false);
        setIsVideoEnabled(stream.getVideoTracks()[0]?.enabled || false);

      } catch (error) {
        console.error('Error accessing media devices:', error);
        
        // Show a clear permission error message
        alert('Camera or microphone access denied. Please check your browser permissions and try again.');
      }
    };
    
    getUserMedia();
    
    // Fetch stream details
    fetchStreamDetails();
    
    // Initialize socket connection
    const newSocket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 30,
      reconnectionDelay: 1000,
      timeout: 60000,
      autoConnect: true,
      secure: true,
      forceNew: true,
    });
    setSocket(newSocket);
    
    // Join stream room immediately when socket connects
    newSocket.on('connect', () => {
      console.log('Socket connected, joining stream:', streamId);
      newSocket.emit('join-stream', { streamId, username });
    });
    
    // Fetch existing comments
    fetchComments();
    
    return () => {
      if (peerRef.current) {
        peerRef.current.destroy();
      }
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [streamId, username, navigate]);
  
  // Function to fetch transcripts
  const fetchTranscripts = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/transcriptions/stream/${streamId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTranscripts(data.transcript || []);
      }
    } catch (error) {
      console.error('Error fetching transcripts:', error);
    }
  };

  useEffect(() => {
    if (!socket) return;
    
    // Fetch transcripts initially
    fetchTranscripts();
    
    // Listen for user joined event
    socket.on('user-joined', ({ signal, callerID }) => {
      // If we receive a signal, it means the host is sending us their stream
      if (signal) {
        // Around line 446 in the user-joined event handler
        const peer = new Peer({
          initiator: false,
          trickle: false,
          stream: localStream // Send our stream to the host
        });
        
        // Add this logging
        console.log('Sending local stream to host:', {
          hasStream: !!localStream,
          videoTracks: localStream?.getVideoTracks().length,
          audioTracks: localStream?.getAudioTracks().length,
          videoEnabled: localStream?.getVideoTracks()[0]?.enabled,
          audioEnabled: localStream?.getAudioTracks()[0]?.enabled
        });
        
        peer.on('signal', signal => {
          socket.emit('returning-signal', { callerID, signal });
        });
        
        peer.on('stream', stream => {
          console.log('Received stream from host', stream);
          
          // Store the host's stream
          setHostStream(stream);
          
          // Add host to participants list
          setParticipants([{
            id: callerID,
            username: stream?.hostUsername || 'Host',
            isHost: true,
            stream: stream,
            audioMuted: false,
            videoMuted: false
          }]);
          
          // Also set to video ref as fallback
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        });
        
        peer.signal(signal);
        peerRef.current = peer;
      }
    });
    
    // Listen for viewer count updates
    // Listen for viewer count updates
    socket.on('viewer-count-update', ({ count, viewers }) => {
      console.log('Viewer count update:', { count, viewers });
      if (Array.isArray(viewers)) {
        // Store current viewers for comparison
        setViewers(prevViewers => {
          // Find new viewers by comparing current viewers with the updated list
          const prevViewerUsernames = prevViewers.map(v => v.username);
          const currentViewerUsernames = viewers.map(v => v.username);
          
          // Find viewers that are in the new list but not in the previous list
          const newViewerUsernames = currentViewerUsernames
            .filter(username => !prevViewerUsernames.includes(username));
          
          // Mark new viewers
          const updatedViewers = viewers.map(viewer => ({
            ...viewer,
            isNew: newViewerUsernames.includes(viewer.username)
          }));
          
          // Clear the highlighting after 2 seconds if there are new viewers
          if (newViewerUsernames.length > 0) {
            setTimeout(() => {
              setViewers(prevViewers => 
                prevViewers.map(viewer => ({...viewer, isNew: false}))
              );
            }, 2000);
          }
          
          return updatedViewers;
        });
        
        setViewerCount(count);
      }
    });
    
    // Add listener for viewer left event
    socket.on('viewer-left', ({ username }) => {
      console.log(`Viewer left: ${username}`);
      // You could add additional UI feedback here if desired
    });
    
    // Listen for new comments
    socket.on('new-comment', (comment) => {
      setComments(prevComments => {
        // Ensure prevComments is an array
        const currentComments = Array.isArray(prevComments) ? prevComments : [];
        // Check if comment already exists to prevent duplicates
        const commentExists = currentComments.some(c => 
          c._id === comment._id || 
          (c.username === comment.username && c.text === comment.text && c.timestamp === comment.timestamp)
        );
        return commentExists ? currentComments : [...currentComments, comment];
      });
      // Automatically show comments when a new one is received
      setShowComments(true);
    });
    
    // Listen for like updates
    socket.on('like-update', ({ count, likes }) => {
      setLikeCount(count);
      setLikes(likes);
      
      // Check if current user has liked
      setHasLiked(likes.some(like => like.username === username));
    });
    
    // Add screen sharing event listeners
    socket.on('screen-sharing-started', () => {
      console.log('Host started screen sharing');
      setIsScreenSharing(true);
      
      // Add a notification for users
      const notification = document.createElement('div');
      notification.className = 'screen-sharing-notification';
      notification.textContent = 'Host started screen sharing';
      document.body.appendChild(notification);
      
      // Remove notification after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
    });
    
    socket.on('screen-sharing-stopped', () => {
      console.log('Host stopped screen sharing');
      setIsScreenSharing(false);
      
      // Add a notification for users
      const notification = document.createElement('div');
      notification.className = 'screen-sharing-notification';
      notification.textContent = 'Host stopped screen sharing';
      document.body.appendChild(notification);
      
      // Remove notification after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
    });
    
    // Add listeners for viewer screen sharing events
    socket.on('viewer-screen-sharing-started', ({ username }) => {
      console.log(`Viewer ${username} started screen sharing`);
      // You could add UI feedback here
    });
    
    socket.on('viewer-screen-sharing-stopped', () => {
      console.log('Viewer stopped screen sharing');
      // You could add UI feedback here
    });
    
    // Listen for permissions updates from server
    socket.on('permissions-updated', ({ permissions }) => {
      console.log('Permissions updated:', permissions);
      setPermissions(permissions);
      
      // If permissions were revoked, enforce them
      if (!permissions.allowViewerAudio && isAudioEnabled) {
        toggleAudio();
      }
      
      if (!permissions.allowViewerVideo && isVideoEnabled) {
        toggleVideo();
      }
    });
    
    // Listen for permission denied events
    socket.on('permission-denied', ({ action, message }) => {
      console.log('Permission denied:', action, message);
      setPermissionError({ action, message });
      
      // Clear error after 3 seconds
      setTimeout(() => setPermissionError(null), 3000);
      
      // Revert UI state based on denied action
      if (action === 'audio') {
        setIsAudioEnabled(false);
      } else if (action === 'video') {
        setIsVideoEnabled(false);
      } else if (action === 'screen-sharing') {
        setIsScreenSharing(false);
      }
    });
    
    // Listen for new transcript segments
    socket.on('new-transcript-segment', (segment) => {
      setTranscripts(prev => [...prev, segment]);
    });
    
    return () => {
      socket.off('user-joined');
      socket.off('viewer-count-update');
      socket.off('new-comment');
      socket.off('like-update');
      socket.off('screen-sharing-started');
      socket.off('screen-sharing-stopped');
      socket.off('viewer-screen-sharing-started');
      socket.off('viewer-screen-sharing-stopped');
      socket.off('permissions-updated');
      socket.off('permission-denied');
      socket.off('new-transcript-segment');
    };
  }, [socket, username, localStream]);
  
  const fetchStreamDetails = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/streams/${streamId}`);
      if (!response.ok) {
        throw new Error('Stream not found');
      }
      const data = await response.json();
      setStream(data);
      setLikes(data.likes);
      setLikeCount(data.likes.length);
      setHasLiked(data.likes.some(like => like.username === username));
    } catch (error) {
      console.error('Error fetching stream details:', error);
      alert('Stream not found or has ended.');
      navigate('/');
    }
  };
  
  
  // Modify the fetchComments function to ensure it's called when needed
  const fetchComments = async () => {
    try {
      console.log('Fetching comments for stream:', streamId);
      const response = await fetch(`${SERVER_URL}/comments/${streamId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Comments fetched:', data);
        setComments(data);
      } else {
        console.error('Error response when fetching comments:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };
  
  // Make sure the toggleComments function fetches comments before showing them
  const toggleComments = () => {
    // If we're about to show comments, fetch the latest
    if (!showComments) {
      fetchComments();
    }
    // Toggle visibility
    setShowComments(prev => !prev);
  };
  
  const sendComment = async () => {
    if (!commentText.trim()) return;
    
    if (!permissions.allowViewerChat) {
      setPermissionError({
        action: 'chat',
        message: 'Chat is disabled by the host'
      });
      setTimeout(() => setPermissionError(null), 3000);
      return;
    }
    
    const commentData = {
      username,
      text: commentText
    };
    
    try {
      // Send via REST API for persistence
      const response = await fetch(`${SERVER_URL}/comments/${streamId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(commentData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const savedComment = await response.json();
      console.log('Comment saved via API:', savedComment);
      
      // Also emit via socket for real-time updates to other users
      if (socket) {
        socket.emit('send-comment', {
          streamId,
          ...commentData
        });
      }
      
      // No longer update UI here as it will be handled by the socket event
      // to prevent duplicates
      
      setCommentText('');
    } catch (error) {
      console.error('Error saving comment:', error);
      alert('Failed to save comment. Please try again.');
    }
  };
  
  const handleLike = async () => {
    if (hasLiked) return;
    
    try {
      // Use socket for real-time updates instead of REST API
      if (socket) {
        socket.emit('send-like', {
          streamId,
          username
        });
      }
      
      // Optimistically update UI
      setHasLiked(true);
      setLikeCount(prev => prev + 1);
    } catch (error) {
      console.error('Error sending like:', error);
    }
  };
  
const leaveStream = () => {
  // Show the modal instead of window.confirm
  setShowLeaveModal(true);
};

const confirmLeave = () => {
  // Clean up media streams before navigating
  if (localStream) {
    localStream.getTracks().forEach(track => track.stop());
  }
  
  // Clean up peer connection
  if (peerRef.current) {
    peerRef.current.destroy();
  }
  
  // Clean up socket connection
  if (socket) {
    socket.disconnect();
  }
  
  // Clean up recording if active
  if (recordingTimerRef.current) {
    clearInterval(recordingTimerRef.current);
  }
  if (mediaRecorderRef.current && isRecording) {
    mediaRecorderRef.current.stop();
  }

  // Navigate back to home
  navigate('/');
};

const cancelLeave = () => {
  setShowLeaveModal(false);
};
// Move this useEffect before any conditional returns
useEffect(() => {
  return () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
    }
  };
}, [isRecording]);

  if (!stream) {
    return <div>Loading...</div>;
  }
  
  const toggleAudio = () => {
    if (!permissions.allowViewerAudio && !isAudioEnabled) {
      setPermissionError({
        action: 'audio',
        message: 'Audio is disabled by the host'
      });
      setTimeout(() => setPermissionError(null), 3000);
      return;
    }
    
    if (localStream && localStream.getAudioTracks().length > 0) {
      const audioTrack = localStream.getAudioTracks()[0];
      audioTrack.enabled = !audioTrack.enabled;
      setIsAudioEnabled(audioTrack.enabled);
      
      // Notify server about media status change
      if (socket) {
        socket.emit('media-status-update', {
          streamId,
          isAudioEnabled: audioTrack.enabled,
          isVideoEnabled: isVideoEnabled
        });
      }
    }
  };
  
  const toggleVideo = () => {
    if (!permissions.allowViewerVideo && !isVideoEnabled) {
      setPermissionError({
        action: 'video',
        message: 'Video is disabled by the host'
      });
      setTimeout(() => setPermissionError(null), 3000);
      return;
    }
    
    if (localStream && localStream.getVideoTracks().length > 0) {
      const videoTrack = localStream.getVideoTracks()[0];
      videoTrack.enabled = !videoTrack.enabled;
      setIsVideoEnabled(videoTrack.enabled);
      
      // Notify server about media status change
      if (socket) {
        socket.emit('media-status-update', {
          streamId,
          isAudioEnabled: isAudioEnabled,
          isVideoEnabled: videoTrack.enabled
        });
      }
    }
  };
  
  // Function to toggle screen sharing
  const toggleScreenSharing = async () => {
    if (!permissions.allowViewerScreenShare && !isScreenSharing) {
      setPermissionError({
        action: 'screen-sharing',
        message: 'Screen sharing is disabled by the host'
      });
      setTimeout(() => setPermissionError(null), 3000);
      return;
    }
    
    try {
      if (isScreenSharing) {
        // Stop screen sharing
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => track.stop());
          screenStreamRef.current = null;
        }
        setIsScreenSharing(false);
        
        // Notify other users
        if (socket) {
          socket.emit('viewer-screen-sharing-stopped', { streamId });
        }
        
        // Revert to the original stream in the peer connection
        if (peerRef.current && localStream) {
          // Replace tracks with original camera stream
          const videoTrack = localStream.getVideoTracks()[0];
          
          // Get all video senders in the peer connection
          const senders = peerRef.current.getSenders();
          const videoSender = senders.find(sender => 
            sender.track && sender.track.kind === 'video'
          );
          
          if (videoSender && videoTrack) {
            console.log('Replacing screen sharing track with camera track');
            videoSender.replaceTrack(videoTrack);
          }
        }
      } else {
        // Start screen sharing
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always'
          },
          audio: false
        });
        
        screenStreamRef.current = screenStream;
        setIsScreenSharing(true);
        
        // Handle when user stops sharing via browser controls
        screenStream.getVideoTracks()[0].onended = () => {
          setIsScreenSharing(false);
          screenStreamRef.current = null;
          
          // Notify other users
          if (socket) {
            socket.emit('viewer-screen-sharing-stopped', { streamId });
          }
          
          // Revert to the original stream in the peer connection
          if (peerRef.current && localStream) {
            // Replace tracks with original camera stream
            const videoTrack = localStream.getVideoTracks()[0];
            
            // Get all video senders in the peer connection
            const senders = peerRef.current.getSenders();
            const videoSender = senders.find(sender => 
              sender.track && sender.track.kind === 'video'
            );
            
            if (videoSender && videoTrack) {
              console.log('Replacing screen sharing track with camera track');
              videoSender.replaceTrack(videoTrack);
            }
          }
        };
        
        // Notify other users
        if (socket) {
          socket.emit('viewer-screen-sharing-started', { streamId, username });
        }
        
        // Update the peer connection with the screen sharing stream
        if (peerRef.current) {
          // Replace the video track with the screen sharing track
          const videoTrack = screenStream.getVideoTracks()[0];
          
          // Get all video senders in the peer connection
          const senders = peerRef.current.getSenders();
          const videoSender = senders.find(sender => 
            sender.track && sender.track.kind === 'video'
          );
          
          if (videoSender && videoTrack) {
            console.log('Replacing camera track with screen sharing track');
            videoSender.replaceTrack(videoTrack);
          } else {
            console.error('No video sender found in peer connection');
          }
        }
      }
    } catch (error) {
      console.error('Error toggling screen sharing:', error);
      alert('Failed to start screen sharing. Please try again.');
    }
  };
  
  // Function to start recording
  const startRecording = () => {
    if (!videoRef.current || !videoRef.current.srcObject) {
      alert('No stream available to record');
      return;
    }

    try {
      const mediaRecorder = new MediaRecorder(videoRef.current.srcObject, {
        mimeType: 'video/webm;codecs=vp9,opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      setRecordedChunks([]);
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordedChunks((prev) => [...prev, event.data]);
        }
      };
      
      mediaRecorder.onstop = handleRecordingStop;
      
      // Start the recording
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      
      // Start a timer to track recording duration
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prevTime => prevTime + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording. Your browser may not support this feature.');
    }
  };
  
  // Function to stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(recordingTimerRef.current);
    }
  };
  
  // Handle recording stop and upload
  const handleRecordingStop = async () => {
    setIsRecording(false);
    
    try {
      // Create a blob from the recorded chunks
      const blob = new Blob(recordedChunks, { type: 'video/webm' });
      
      // Create form data for upload
      const formData = new FormData();
      formData.append('video', blob, `stream_${streamId}_${Date.now()}.webm`);
      formData.append('streamId', streamId);
      formData.append('title', stream.title);
      formData.append('username', username);
      formData.append('duration', recordingTime);
      
      // Upload the recording
      const response = await fetch(`${SERVER_URL}/recordings`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        alert('Recording saved successfully!');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save recording');
      }
    } catch (error) {
      console.error('Error saving recording:', error);
      alert('Failed to save recording: ' + error.message);
    }
    
    // Clear recorded chunks
    setRecordedChunks([]);
  };
  
  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  // Clean up recording and screen sharing on component unmount
  // eslint-disable-next-line react-hooks/rules-of-hooks
  
  // Download recorded video
  const downloadRecording = () => {
    if (recordedChunks.length === 0) {
      alert('No recording available to download');
      return;
    }
    
    const blob = new Blob(recordedChunks, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style = 'display: none';
    a.href = url;
    a.download = `stream_recording_${Date.now()}.webm`;
    a.click();
    
    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };
  
  
  return (
    <StreamContainer>
      <GlobalStyle />
      {permissionError && (
        <PermissionErrorMessage>
          {permissionError.message}
        </PermissionErrorMessage>
      )}
      <StreamHeader>
        <BackButton onClick={leaveStream}>
          <FaArrowLeft /> Leave Stream
        </BackButton>
        <StreamTitle><MeetingInfoPrefix>Meeting info:</MeetingInfoPrefix> {stream?.title || 'Loading...'}</StreamTitle>
        <StreamHost>Host: {stream?.hostUsername || 'Unknown'}</StreamHost>
      </StreamHeader>
      
      <MainContent>
        <VideoSection>
          {/* Replace the single video with VideoGrid */}
          <VideoGrid 
            participants={participants} 
            localStream={localStream} 
            isHost={false} 
            isScreenSharing={isScreenSharing}
          />
          
          {/* Keep your existing video element as a fallback but hide it */}
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            style={{ display: 'none' }} 
            onClick={() => videoRef.current && videoRef.current.play()}
          />
          
          {isScreenSharing && (
            <ScreenSharingIndicator>
              <FaDesktop />
              <span>Screen sharing active</span>
            </ScreenSharingIndicator>
          )}
          
          <StreamControls>
            <ControlItem>
              <FaUserFriends />
              <span>{viewerCount} viewers</span>
            </ControlItem>
            <LikeButton onClick={handleLike} liked={hasLiked}>
              <FaThumbsUp />
              <span>{likeCount} likes</span>
            </LikeButton>
            <ControlItem onClick={toggleComments} style={{ cursor: 'pointer' }}>
              <FaComment />
              <span>{comments.length} comments</span>
            </ControlItem>
            <ControlItem onClick={() => setShowTranscripts(!showTranscripts)} style={{ cursor: 'pointer' }}>
              <FaClosedCaptioning />
              <span>Transcript</span>
            </ControlItem>
            
            {/* Add recording controls */}
            <RecordingControls>
              {isRecording ? (
                <>
                  <RecordingIndicator />
                  <RecordingTime>{formatTime(recordingTime)}</RecordingTime>
                  <MediaButton onClick={stopRecording} title="Stop Recording">
                    <FaStop />
                  </MediaButton>
                </>
              ) : (
                <MediaButton onClick={startRecording} title="Start Recording">
                  <FaRecordVinyl />
                </MediaButton>
              )}
            </RecordingControls>
            
            <MediaControlsContainer>
                {localStream && (
                  <>
                    <MediaButton onClick={toggleAudio} title={isAudioEnabled ? 'Mute Microphone' : 'Unmute Microphone'}>
                      {isAudioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
                    </MediaButton>
                    <MediaButton onClick={toggleVideo} title={isVideoEnabled ? 'Turn Off Camera' : 'Turn On Camera'}>
                      {isVideoEnabled ? <FaVideo /> : <FaVideoSlash />}
                    </MediaButton>
                    <MediaButton 
                      onClick={toggleScreenSharing} 
                      title={isScreenSharing ? 'Stop Screen Sharing' : 'Share Screen'}
                      style={{ background: isScreenSharing ? '#ff4d4f' : '#f0f2f5' }}
                    >
                      <FaDesktop />
                    </MediaButton>
                  </>
                )}
              </MediaControlsContainer>
          </StreamControls>
          
          {/* Comment Popup */}
          {showComments && (
            <CommentPopup>
              <CommentPopupHeader>
                <CommentPopupTitle>Comments</CommentPopupTitle>
                <CloseButton onClick={toggleComments}>
                  <FaTimes />
                </CloseButton>
              </CommentPopupHeader>
              <CommentsList>
                {comments.map((comment, index) => (
                  <CommentItem key={index}>
                    <CommentUser>{comment.username}</CommentUser>
                    <CommentText>{comment.text}</CommentText>
                  </CommentItem>
                ))}
              </CommentsList>
              <CommentInput>
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  onKeyPress={(e) => e.key === 'Enter' && sendComment()}
                />
                <button onClick={sendComment}>Send</button>
              </CommentInput>
            </CommentPopup>
          )}
          
          {/* Transcript Popup */}
          {showTranscripts && (
            <TranscriptPanel>
              <TranscriptHeader>
                <h3>Transcript</h3>
                <CloseButton onClick={() => setShowTranscripts(false)}>
                  <FaTimes />
                </CloseButton>
              </TranscriptHeader>
              <TranscriptContent>
                {transcripts.length > 0 ? (
                  transcripts.map((segment, index) => (
                    <TranscriptSegment key={index}>
                      <TranscriptSpeaker>{segment.speakerUsername}</TranscriptSpeaker>
                      <TranscriptTime>
                        {new Date(segment.timestamp).toLocaleTimeString()}
                      </TranscriptTime>
                      <TranscriptText>{segment.text}</TranscriptText>
                    </TranscriptSegment>
                  ))
                ) : (
                  <EmptyTranscript>No transcript available</EmptyTranscript>
                )}
              </TranscriptContent>
            </TranscriptPanel>
          )}
        </VideoSection>
        
        <SidePanel>
          <ViewersList>
            <h3><FaUserFriends /> Viewers ({viewerCount})</h3>
            <ul>
              {Array.isArray(viewers) && viewers.length > 0 ? (
                viewers.map((viewer, index) => (
                  <ViewerItem 
                    key={viewer.socketId || index}
                    className={viewer.isNew ? 'new-viewer' : ''}
                  >
                    {viewer.username}
                  </ViewerItem>
                ))
              ) : (
                <li>No viewers yet</li>
              )}
            </ul>
          </ViewersList>
        </SidePanel>
      </MainContent>
      
      {/* Leave Stream Confirmation Modal */}
      {showLeaveModal && (
        <ModalOverlay>
          <ConfirmationModal>
            <ModalContent>
              <ModalTitle>Are you sure you want to end the Meeting?</ModalTitle>
              <ModalSubtext>You will be disconnected from this stream.</ModalSubtext>
              <ModalButtonGroup>
                <CancelButton onClick={() => setShowLeaveModal(false)}>
                  Cancel
                </CancelButton>
                <LeaveButton onClick={confirmLeave}>
                  Yes, End Meeting
                </LeaveButton>
              </ModalButtonGroup>
            </ModalContent>
          </ConfirmationModal>
        </ModalOverlay>
      )}
    </StreamContainer>
  );
};

// Add styled component for screen sharing indicator
const ScreenSharingIndicator = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  z-index: 10;
  
  svg {
    font-size: 16px;
  }
`;

const PermissionErrorMessage = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 77, 77, 0.9);
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.3s, fadeOut 0.3s 2.7s;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translate(-50%, -20px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; transform: translate(-50%, 0); }
    to { opacity: 0; transform: translate(-50%, -20px); }
  }
`;

// Transcript styled components
const TranscriptPanel = styled.div`
  position: absolute;
  bottom: 70px;
  right: 20px;
  width: 350px;
  height: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  z-index: 100;
  overflow: hidden;
`;

const TranscriptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  border-bottom: 1px solid #ddd;
  
  h3 {
    margin: 0;
    font-size: 16px;
  }
`;

const TranscriptContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px 15px;
`;

const TranscriptSegment = styled.div`
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
`;

const TranscriptSpeaker = styled.div`
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 5px;
`;

const TranscriptTime = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 5px;
`;

const TranscriptText = styled.div`
  font-size: 14px;
  line-height: 1.4;
`;

const EmptyTranscript = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #888;
  font-style: italic;
`;

export default ViewStream;

